// Централизованная очистка session-scoped storage при logout.
// Закрывает Issue #3 (утечка drafts между аккаунтами в одном браузере).
//
// Контракт:
//   - Из localStorage и sessionStorage удаляются все ключи, чьё имя
//     начинается с одного из SESSION_NAMESPACE_PREFIXES.
//   - Ключи из SESSION_KEEP_KEYS не трогаются — это analytics consent
//     и device-level политики (см. registry).
//   - Best-effort: ловим QuotaExceededError / SecurityError (private mode),
//     не падаем. Lockout пользователя при logout недопустим.
//
// Вызывается из:
//   - mutation `clearUser` (frontend/store/index.ts) — этим закрываются оба
//     пути: explicit logout-action и любое программное очищение user'а.
//   - 401-refresh-failure в httpClient — там тоже инвалидируется сессия.
//     (Wave 1 этого PR'а не меняет 401-поведение; см. план.)

import {
    SESSION_KEEP_KEYS,
    SESSION_NAMESPACE_PREFIXES,
} from '@/shared/config/sessionNamespaces';

const isSessionScopedKey = (key: string): boolean => {
    if (SESSION_KEEP_KEYS.includes(key)) return false;
    for (const prefix of SESSION_NAMESPACE_PREFIXES) {
        if (key.startsWith(prefix)) return true;
    }
    return false;
};

const listKeys = (storage: Storage): string[] => {
    const out: string[] = [];
    for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (k != null) out.push(k);
    }
    return out;
};

const clearMatchingKeys = (storage: Storage): void => {
    // Снимаем snapshot ключей до удаления — Storage API живой, мутировать
    // его во время итерации опасно (индексы поедут).
    const all = listKeys(storage);
    for (const key of all) {
        if (!isSessionScopedKey(key)) continue;
        try {
            storage.removeItem(key);
        } catch {
            // best-effort: SecurityError в private-mode и т.п.
        }
    }
};

/**
 * Удаляет все session-scoped ключи из localStorage и sessionStorage.
 * Безопасно вызывать многократно; idempotent.
 */
export const clearAllSessionData = (): void => {
    if (typeof window === 'undefined') return;

    try {
        clearMatchingKeys(window.localStorage);
    } catch {
        // localStorage целиком недоступен — пропускаем.
    }

    try {
        clearMatchingKeys(window.sessionStorage);
    } catch {
        // sessionStorage целиком недоступен — пропускаем.
    }
};
